import React, { useState, useEffect } from 'react';
import { db, functions, useAuth } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { Radio, Send, Siren, CheckCircle, Clock, Info, History } from 'lucide-react';

interface PushLog {
    id: string;
    action: string;
    adminName: string;
    details: string;
    timestamp: any;
}

export const AdminPushPanel: React.FC = () => {
    const { user } = useAuth();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [url, setUrl] = useState('');

    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [logs, setLogs] = useState<PushLog[]>([]);
    const [limitCount, setLimitCount] = useState(20);

    useEffect(() => {
        if (!db) return;
        const q = query(
            collection(db, 'admin_logs'),
            orderBy('timestamp', 'desc'),
            limit(limitCount)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const pushLogs = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as PushLog))
                .filter(log => log.action === 'SEND_PUSH_NOTIFICATION');
            setLogs(pushLogs);
        });

        return () => unsubscribe();
    }, [limitCount]);

    const handleSendPush = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);

        if (!title.trim() || !body.trim()) {
            setError("Título e Mensagem são obrigatórios.");
            return;
        }

        if (!confirm(`Deseja disparar essa notificação PARA TODOS os jogadores cadastrados agora?\n\nTítulo: ${title}\nMensagem: ${body}`)) return;

        setIsSending(true);

        try {
            if (!functions) throw new Error("Functions não inicializado.");
            const sendPush = httpsCallable(functions, 'sendAdminPushNotification');

            const result = await sendPush({
                title: title.trim(),
                body: body.trim(),
                url: url.trim() || '/'
            });

            const data = result.data as any;

            if (data.success) {
                setSuccessMessage(`Enviado para ${data.sentCount} dispositivos. Falhas: ${data.failureCount}.`);
                setTitle('');
                setBody('');
                setUrl('');
            } else {
                setError(data.message || "Falha desconhecida.");
            }
        } catch (err: any) {
            console.error("Erro ao enviar push:", err);
            setError(err.message || "Erro de comunicação com o servidor.");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center border-b border-noir-800 pb-4">
                <h1 className="text-2xl font-bold font-mono tracking-widest text-noir-accent flex items-center gap-3">
                    <Radio className="w-6 h-6" /> CENTRAL PUSH
                </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Form Section */}
                <div className="bg-noir-900 border border-noir-800 rounded-lg p-6">
                    <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                        <Send className="w-5 h-5 text-noir-muted" /> NOVO DISPARO
                    </h2>

                    <p className="text-sm text-noir-muted mb-6 bg-black/30 p-3 rounded flex items-start gap-2 border-l-2 border-amber-600">
                        <Info className="w-4 h-4 mt-0.5 shrink-0" />
                        Esta mensagem será enviada a todos os detetives que autorizaram o recebimento de notificações no navegador (Desktop/Android Mobile).
                    </p>

                    {error && (
                        <div className="bg-red-900/20 border border-red-500/50 text-red-500 p-3 rounded mb-4 text-sm flex items-center gap-2">
                            <Siren className="w-4 h-4" /> {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="bg-green-900/20 border border-green-500/50 text-green-500 p-3 rounded mb-4 text-sm flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" /> {successMessage}
                        </div>
                    )}

                    <form onSubmit={handleSendPush} className="space-y-4">
                        <div>
                            <label className="block text-xs uppercase text-noir-muted font-bold mb-1">Título do Alerta *</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full bg-black/50 border border-noir-700 rounded p-2 text-white outline-none focus:border-noir-accent transition-colors"
                                placeholder="Ex: NOVO CASO LIBERADO!"
                                maxLength={50}
                            />
                            <p className="text-[10px] text-right text-noir-600 mt-1">{title.length}/50</p>
                        </div>

                        <div>
                            <label className="block text-xs uppercase text-noir-muted font-bold mb-1">Corpo da Mensagem *</label>
                            <textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                className="w-full h-24 bg-black/50 border border-noir-700 rounded p-2 text-white outline-none focus:border-noir-accent transition-colors resize-none"
                                placeholder="Descreva brevemente o aviso..."
                                maxLength={150}
                            />
                            <p className="text-[10px] text-right text-noir-600 mt-1">{body.length}/150</p>
                        </div>

                        <div>
                            <label className="block text-xs uppercase text-noir-muted font-bold mb-1">URL de Destino (Opcional)</label>
                            <input
                                type="text"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                className="w-full bg-black/50 border border-noir-700 rounded p-2 text-white outline-none focus:border-noir-accent transition-colors font-mono text-sm"
                                placeholder="/game"
                            />
                            <p className="text-[10px] text-noir-600 mt-1">Ao clicar no push, o navegador abrirá esta URL.</p>
                        </div>

                        <button
                            type="submit"
                            disabled={isSending}
                            className={`w-full py-3 rounded font-bold uppercase tracking-wider flex justify-center items-center gap-2 transition-all mt-6 ${isSending ? 'bg-amber-900/50 text-amber-500/50 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-500 text-black shadow-[0_0_15px_rgba(217,119,6,0.3)]'}`}
                        >
                            {isSending ? 'Transmitindo...' : <><Send className="w-4 h-4" /> TRANSMITIR PARA TODOS</>}
                        </button>
                    </form>
                </div>

                {/* Log Section */}
                <div className="bg-noir-900 border border-noir-800 rounded-lg p-6 flex flex-col h-[600px]">
                    <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                        <History className="w-5 h-5 text-noir-muted" /> HISTÓRICO DE TRANSMISSÃO
                    </h2>

                    <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 pr-2">
                        {logs.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-noir-600 opacity-50">
                                <Radio className="w-8 h-8 mb-2" />
                                <p className="text-sm">Nenhuma transmissão registrada.</p>
                            </div>
                        ) : (
                            logs.map(log => (
                                <div key={log.id} className="bg-black/30 p-3 rounded border border-noir-800 text-sm">
                                    <div className="flex justify-between items-start mb-2 border-b border-noir-800/50 pb-2">
                                        <div className="flex items-center gap-2 text-noir-400">
                                            <div className="bg-amber-900/30 text-amber-500 p-1.5 rounded"><Radio className="w-3 h-3" /></div>
                                            <span className="font-mono text-xs text-white">{log.adminName}</span>
                                        </div>
                                        <span className="text-[10px] text-noir-600 flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {log.timestamp?.toDate().toLocaleString('pt-BR')}
                                        </span>
                                    </div>
                                    <p className="text-zinc-300 font-mono text-sm whitespace-pre-wrap mt-2 bg-black/40 p-2 rounded border border-noir-800/30 leading-relaxed shadow-inner">
                                        {log.details}
                                    </p>
                                </div>
                            ))
                        )}
                        {logs.length >= limitCount && (
                            <button
                                onClick={() => setLimitCount(prev => prev + 20)}
                                className="w-full mt-4 py-3 border border-noir-800 text-noir-400 font-bold hover:text-white hover:bg-noir-800/50 rounded text-xs transition-colors tracking-widest uppercase"
                            >
                                CARREGAR MAIS REGISTROS
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
